import { DiscardCloneJobRequestSchema, DiscardCloneJobResponseSchema } from './discard-clone-job.schema';
/**
 * Discard Clone Job Endpoint Contract
 * Rolls back an interrupted or failed clone. Restricted to super-admins.
 *
 * This is the ONLY thing in the system that destroys a half-built clone — the
 * reaper parks jobs and never drops anything, because no timer can tell dead
 * work from slow work. Destruction is an explicit, attributable admin action.
 *
 * Every guard below holds before a single byte is dropped, and a guard that
 * fails refuses the whole request (409, `TENANT_CLONE_DISCARD_REFUSED`) rather
 * than half-rolling-back:
 *
 * 1. The job is `interrupted` or `failed`. `running` is live work, and
 *    `importFailed`/`completed` mean the clone succeeded and its tenant was
 *    KEPT — a rollback of either is a destruction, not a compensation.
 * 2. The job's own liveness stamp is older than the staleness window.
 *    `interrupted` is a TIMER'S GUESS, not proof the worker died, and a worker
 *    that is demonstrably still stamping must not have its database dropped
 *    underneath it.
 * 3. The job's `tenantId` is a real 24-hex ObjectId. The SuperAdmin registry is
 *    reached by a literal sentinel string rather than an id, so an unguarded
 *    drop of a non-ObjectId target takes out the registry database.
 * 4. `tenantId !== sourceTenantId` — never destroy the tenant being copied.
 * 5. Ownership proof by nonce: the tenant row must still carry the exact random
 *    nonce written when the job started. A nonce is checked, not inferred —
 *    matching a slug, name, or creation timestamp is not proof of ownership.
 *    The clone RELEASES the nonce when it hands the tenant over, so a completed
 *    clone fails this guard permanently, whatever its tenant's `active` value.
 * 6. The tenant is `active: false`. Defence in depth behind the nonce, not the
 *    load-bearing guard: `active` is a routine, caller-settable admin field
 *    (`PATCH /tenants/:id` exposes it), so it is a state, not ownership proof.
 *
 * Runs synchronously: the rollback is a handful of deletes, not a 65 s copy.
 */ export const discardCloneJobContract = {
    method: 'POST',
    path: '/api/v2/tenant-clone-jobs/:jobId/discard',
    description: 'Roll back an interrupted or failed tenant clone (super-admin only)',
    requestSchema: DiscardCloneJobRequestSchema,
    responseSchema: DiscardCloneJobResponseSchema,
    statusCodes: {
        success: 200,
        errors: {
            400: 'Invalid job id',
            401: 'Unauthorized - user not authenticated',
            403: 'Forbidden - user is not a super-admin',
            404: 'Clone job not found',
            409: 'Discard refused by a guard (TENANT_CLONE_DISCARD_REFUSED) - nothing was dropped'
        }
    }
};

//# sourceMappingURL=discard-clone-job.contract.js.map